import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { commonComponentImages } from '@images/common-components-images';
import { Pagination } from '@models/pagination.model';
import { ApiService } from '@services/api/api.service';
import { ListingApprovalService } from '@services/api/listing-approval.service';
import { ListingPropertiesService } from '@services/api/listing-properties.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { ToasterService } from '@services/util/toaster.service';
import moment from 'moment';
import { Subject } from 'rxjs';
import { IActivePost, IPostComment } from '../listing-preview-models';
import { PreviewListingComponent } from '../preview-listing/preview-listing.component';

@Component({
  selector: 'app-approvel-comments',
  templateUrl: './approvel-comments.component.html',
  styleUrls: ['./approvel-comments.component.scss'],
})
export class ApprovelCommentsComponent implements OnChanges {
  @ViewChild('previewModel') previewModel: PreviewListingComponent;
  @Input() idx: any = null;
  @Input() cb_afterupdate: (value: any) => void;
  @Input() lisitngDetail: any = null;
  @Input() inOneline: boolean;
  @Input() showPublicComments: boolean;
  @Input() showUserComments: boolean;

  @Input() comments: any[] = [];
  @Input() vendorComments: any[] = [];
  // @Input() permission: Observable<any> = null;
  @Input() readWritePermissions: { can_delete: boolean; can_write: boolean; can_approve: boolean; can_resolve: boolean } = {
    can_approve: false,
    can_delete: false,
    can_resolve: false,
    can_write: false,
  };
  images = commonComponentImages;
  user_name: string = '';
  user_message: string = '';
  vendor_comment: any[] = [];
  public Editor = ClassicEditor;
  tempCommentsList: any[] = [];
  bubbles: IActivePost[] = [];
  public pagination: Pagination;

  constructor(
    public api: ApiService,
    private toasterService: ToasterService,
    private localStorageService: LocalStorageService,
    private _listingApprovalService: ListingApprovalService,
    private listingPropertiesService: ListingPropertiesService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.comments?.currentValue) {
      this.bubbles = changes?.comments.currentValue;
      this.tempCommentsList = this.comments = this.bubbles
        ?.map(result => {
          return result?.comments?.map(commenter => {
            commenter = { ...commenter, label: result?.label, is_resolved: result?.is_resolved };
            return commenter;
          });
        })
        ?.flat()
        ?.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB.getTime() - dateA.getTime();
        })
        ?.sort((a: any, b: any) => {
          if (a?.is_resolved == b?.is_resolved) {
            return 0;
          } else if (a?.is_resolved === false) {
            return -1;
          } else {
            return 1;
          }
        });
      this.pagination = {
        ...this.pagination,
        per_page: 10,
        total: this.tempCommentsList?.length,
        total_pages: Math.ceil(this.tempCommentsList?.length / 10),
      };
    }
    if (changes?.vendorComments?.currentValue) {
      this.vendor_comment = changes?.vendorComments?.currentValue;
    }

    if (changes?.lisitngDetail?.currentValue) {
      let listingId = changes?.lisitngDetail.currentValue?._id;
      listingId && this.getAgentId(listingId);
    }
  }

  listing_preview_id: Subject<any> = new Subject<any>();
  public userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
  first: number = 0;
  rows: number = 10;
  vendCommentsValid;
  agentIds: any[] = [];

  /**
   * Lifecycle hook that runs after component initialization.
   * Calls the getSettings method to fetch settings data.
   */
  ngOnInit(): void {
    this.getSettings();
  }

  /**
   * Fetches the vendor comments visibility settings for the current office.
   * Updates the vendCommentsValid variable with the retrieved data.
   */
  getSettings() {
    this.listingPropertiesService.getVisibility().subscribe(res => {
      if (res.data) {
        this.vendCommentsValid = res.data;
      }
    });
  }

  /**
   * Fetches agent IDs associated with a given listing ID.
   * Updates the agentIds variable with the retrieved agent IDs.
   *
   * @param id - The ID of the listing to fetch agent permissions for.
   */
  getAgentId(id) {
    this._listingApprovalService.getAllowedPermissionListOnListingApproval(id).subscribe(res => {
      this.agentIds = res?.listing_agents;
    });
  }

  /**
   * Displays a preview of the listing.
   * Updates the listing preview ID and shows the preview modal.
   */
  showPreview() {
    this.listing_preview_id.next(this.lisitngDetail?._id);
    this.previewModel.show();
  }

  /**
   * Converts a date string in 'DD-MM-YYYY hh:mm A' format to a JavaScript Date object.
   * Returns an empty string if the conversion fails.
   *
   * @param value - The date string to be converted.
   * @returns The converted Date object or an empty string.
   */
  getMomentDateTime(value: any): any {
    try {
      if (value) {
        return moment(value, 'DD-MM-YYYY hh:mm A').toDate();
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Filters comments based on the user's input.
   * If input exists, filters comments by the commenter's name or message.
   * If input is empty, resets comments to the full list.
   *
   * @param event - The input event from the search field.
   */
  onChangeFilterComments(event) {
    const value = event?.target?.value || '';
    if (value?.length > 0) {
      this.comments = this.tempCommentsList.filter(val => {
        let startsWithCondition =
          val?.commented_by?.toLowerCase().startsWith(value.toLowerCase()) || val?.message?.toLowerCase().startsWith(value.toLowerCase());

        let includesCondition =
          val?.commented_by?.toLowerCase().includes(value.toLowerCase()) || val?.message?.toLowerCase().includes(value.toLowerCase());

        if (startsWithCondition) {
          return startsWithCondition;
        } else if (!startsWithCondition && includesCondition) {
          return includesCondition;
        } else return null;
      });
    } else {
      this.comments = this.tempCommentsList;
    }
    this.pagination = { ...this.pagination, per_page: 10, total: this.comments?.length, total_pages: Math.ceil(this.comments?.length / 10) };
  }

  /**
   * Filters comments based on the resolved status.
   * If a filter is applied, only resolved or unresolved comments are shown.
   * If no filter is applied, resets comments to the full list.
   *
   * @param event - The filter event containing the resolved status.
   */
  onChangeResolvedFilter(event) {
    if (event) {
      this.comments = this.tempCommentsList?.filter(comment => comment?.is_resolved === event?.is_resolved);
    } else {
      this.onChangeFilterComments(null);
    }
  }

  /**
   * Creates or updates a comment in the listing approval process.
   * Sends an API request to save the comment modifications and updates the UI.
   *
   * @param event - The mouse event to stop propagation.
   * @param comment - The comment object to be added or updated.
   */
  createComment(event: MouseEvent, comment: any) {
    event?.stopPropagation();
    let modification_in: { type: 'add' | 'remove' | 'update'; data: any } = null;
    // Object to track modifications (e.g., add, remove, update)
    modification_in = { type: 'update', data: comment };
    // Preparing request parameters
    let param = {
      modification: modification_in,
      office_id: this.api.getOfficeId(),
      listing_id: this.lisitngDetail?._id || '',
      listing_comments: this.bubbles?.map(result => {
        let response = result?.comments?.map(localComment => {
          if (localComment?.id === comment?.id) {
            result = { ...result, is_resolved: comment?.is_resolved };
            return comment;
          }
          return localComment;
        });
        return (result = {
          ...result,
          comments: response,
        });
      }),
    };
    // Sending API request to save the comment
    this._listingApprovalService.saveCommentsOnListingApproval(param).subscribe({
      next: response => {
        const { status, data } = response;
        if (status && data !== null) {
          this.cb_afterupdate(data);
          this.toasterService.Success('Saved successfully.');
        } else {
          this.toasterService.Error('Something went wrong');
        }
      },
      error: () => {
        this.toasterService.Error('Something went wrong');
      },
    });
  }

  /**
   * Scrolls to the specific comment's bubble if it exists and is unresolved.
   * Expands the bubble by triggering a click event.
   */
  triggerToScroll() {
    let activeBubble = this.bubbles.find(bubble => bubble.comments.filter(comment => comment.id === comment.id)?.length > 0);
    if (activeBubble?.id && activeBubble?.is_resolved === false) {
      let singleBubbleBox = document.querySelectorAll(`[data-bubble-ref="${activeBubble?.reference_id}"]`) as NodeListOf<Element>;
      if (singleBubbleBox) {
        singleBubbleBox?.[singleBubbleBox?.length - 1]?.scrollIntoView({ behavior: 'smooth' });
        let singleBubbleBoxTemp = singleBubbleBox?.[singleBubbleBox?.length - 1] as HTMLElement;
        singleBubbleBoxTemp?.click();
      }
    }
  }

  /**
   * Handles the scroll event and dynamically increases the number of rows displayed.
   */
  onScroll(event: Event): void {
    let previousScrollTop = 0;
    let scrollTop: number = 0;
    const element = event.target as HTMLElement;
    scrollTop = element.scrollTop;
    const isScrollingDown = scrollTop > previousScrollTop;
    previousScrollTop = scrollTop;
    if (isScrollingDown && this.comments?.length > this.rows) {
      this.rows = this.rows + 10;
    }
  }

  /**
   * Toggles the resolved status of a comment and updates the resolution status based on user role.
   */
  onChangeCommentStatus(event, comment: any) {
    comment.is_resolved = !comment?.is_resolved;
    let resultAgent = this.agentIds?.toString()?.includes(this.api.getUserId());
    if (resultAgent) {
      comment.is_resolved_by_admin = false;
    } else {
      comment.is_resolved_by_admin = this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;
    }
    this.createComment(event, comment);
  }

  /**
   * Creates a public comment and sends it to the backend API.
   */
  createPublicComment() {
    if (this.user_name) {
      this.vendor_comment.push({ user_name: this.user_name, user_message: this.user_message, created_at: moment().format('DD-MM-YYYY hh:mm:ss A') });
      let param = {
        user_type: 'external',
        listing_id: this.lisitngDetail?._id || '',
        office_id: this.lisitngDetail?.office_id || '',
        vendor_comment: this.vendor_comment,
        vendor_new_comment: {
          user_name: this.user_name,
          user_message: this.user_message,
          created_at: moment().format('DD-MM-YYYY hh:mm:ss A'),
        },
      };
      this._listingApprovalService.saveCommentsOnListingApproval(param).subscribe({
        next: response => {
          this.toasterService.Success('Saved successfully');
          this.cb_afterupdate(response?.data);
          this.user_name = null;
          this.user_message = null;
        },
        error: () => {
          this.toasterService.Error('Something went wrong');
        },
      });
    }
  }

  /**
   * Toggles the visibility of child comment boxes for a specific comment label.
   */
  toggleChildCommentBoxes(event: any, comment: IPostComment, indexer: number) {
    event?.stopPropagation();
    let firstItem = comment?.label !== this.comments?.slice(this.first, this.first + this.rows)?.[indexer - 1]?.label;
    if (firstItem) {
      let elements = document.querySelectorAll(`[data-approval-label="${comment?.label}"]`);
      if (elements?.length > 0) {
        elements?.forEach(elem => {
          if (elem?.getAttribute(`data-approval-id`) !== comment?.id) {
            let isHidden = elem.classList.contains('d-none');
            if (isHidden) {
              elem.classList.remove('d-none');
            } else {
              elem.classList.add('d-none');
            }
          } else if (elem?.getAttribute(`data-approval-id`) === comment?.id) {
            let classList: string = event?.target?.className?.replaceAll(' ng-star-inserted', '');
            if (classList?.trim()?.includes('rotate-chevron')) {
              elem?.querySelector(`.${classList?.trim()?.replaceAll(' ', '.')}`)?.classList?.remove('rotate-chevron');
            } else {
              elem?.querySelector(`.${classList?.trim()?.replaceAll(' ', '.')}`)?.classList?.add('rotate-chevron');
            }
          }
        });
      }
    }
  }

  /**
   * Determines whether the icon should be visible for a specific comment based on its label.
   */
  isIconVisible(comment: IPostComment, indexer: number) {
    return (
      comment?.label !== this.comments?.slice(this.first, this.first + this.rows)?.[indexer - 1]?.label &&
      this.comments?.slice(this.first, this.first + this.rows)?.filter(val => val.label === comment?.label)?.length > 1
    );
  }
}
